/**
 * `PaymentPanel` — ROAD-2, the money on an order. OWNER: web-frontend-engineer.
 *
 * LAW 30: "i also want to accept payment". LAW 31: "he needs to be able to
 * order it and charge the customer on the spot". P-PAY-01 built the port, the
 * two adapters and the ledger; until now none of it had a way in.
 *
 * ── WHERE IT SITS, AND WHY NOT HIGHER ─────────────────────────────────────
 *
 * `SCR-ORDDET` is three regions in one order: header · THE CUT SHEET IN FULL,
 * INLINE · action and history. The cut sheet is the page, so this goes in
 * region 3 — beside the transitions, never among them. That is not layout
 * politeness: `domain/payments.ts` holds the rule that MONEY CHANGES NO ORDER
 * STATE, and a deposit rendered inside the transition control would teach a
 * tailor the opposite. A payment writes a non-transition event and nothing
 * else. The workshop starts cutting when a human says go, never on a webhook.
 *
 * ── WHAT IT REFUSES TO INVENT ─────────────────────────────────────────────
 *
 * There is no default deposit here, no percentage button, no schedule and no
 * refund window — law 32(d), and decision 4 is Paul's and unanswered. The
 * amount is a number a person types. The only arithmetic on this screen is
 * addition of what actually happened, and it comes from the server.
 *
 * Where a typed amount exceeds what is left, the panel does NOT refuse it: it
 * NAMES the arithmetic and lets a tailor decide, the same way the amendment
 * form names what an empty box will do. Refusing would be inventing the rule
 * "a house never takes more than the price", which is Paul's sentence to write.
 *
 * ── AND THE ONE PRE-FILL RULE ─────────────────────────────────────────────
 *
 * `ST-CONFIRM-WINDOW`'s idiom: empty on load, with a LABELLED ONE-ACTION FILL.
 * So "mark it taken" opens with an empty amount and a button that says what it
 * will put there. Never a pre-fill — a figure a person did not type is a figure
 * nobody checked.
 */

import { useRef, useState } from 'react';
import { t, tDynamic } from '@pt/copy';
import { dateInWords, minorFromTyped, money } from '@pt/view';
import { api, idempotencyKey } from '../lib/api.ts';
import { useResource, useSubmission } from '../lib/resource.ts';
import type { PaymentIntentRow, PaymentMethod } from '../lib/contracts.ts';
import { Button, FactLine, FactPair, RovingRadioGroup, Statement, TextInput } from './primitives.tsx';
import { RefusalStatement } from './backoffice.tsx';
import { FetchError, LoadingRegion, OfflineStatement } from './states.tsx';

/* The purposes, spelled here because the web bundle does not import the server.
   `tests/web/event-labels.test.ts` binds this list to the port's own
   `PAYMENT_PURPOSES` so the two cannot drift — which is exactly the check that
   was missing when a third hand-copy of the STATE list invented
   `part_refunded` and killed this panel on the first partial refund. */
const PURPOSES = ['deposit', 'balance', 'alteration', 'other'] as const;

/** The typed box, read once, in the one place that turns text into money. */
function readAmount(text: string): { minor: number; error: null } | { minor: null; error: string } {
  const read = minorFromTyped(text);
  if ('minor' in read) return { minor: read.minor, error: null };
  return { minor: null, error: tDynamic(`pay.amount.${read.refused}`) };
}

export function PaymentPanel({ orderId }: { orderId: string }) {
  const paid = useResource(() => api.payments(orderId), [orderId]);
  const methods = useResource(() => api.paymentMethods(), []);
  const submission = useSubmission();

  const [amount, setAmount] = useState('');
  const [purpose, setPurpose] = useState<string>('deposit');
  const [processor, setProcessor] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [madeLink, setMadeLink] = useState<string | null>(null);
  /* One key per opening of the panel — P-ROAD-IDEM-01. Two intents for one
     deposit is two things a person reconciles against a processor by hand. */
  const key = useRef(idempotencyKey());

  if (paid.phase === 'idle' || paid.phase === 'quiet' || paid.phase === 'skeleton') {
    return <LoadingRegion rows={4} variant="ledger" slow={paid.slow} label={t('pay.heading')} />;
  }
  if (paid.phase === 'offline') return <OfflineStatement />;
  if (paid.phase === 'error' || paid.data === null) return <FetchError onRetry={paid.reload} />;

  const data = paid.data;
  const usable = (methods.data?.methods ?? []).filter((m) => m.ready);
  const unusable = (methods.data?.methods ?? []).filter((m) => !m.ready);
  const chosen = processor ?? usable[0]?.processor ?? null;

  /* The typed amount against what is left. Arithmetic, said out loud — never a
     refusal, and never a figure this screen decided on its own. */
  const read = amount.trim() === '' ? null : minorFromTyped(amount);
  const typedMinor = read !== null && 'minor' in read ? read.minor : null;
  const over = typedMinor !== null && typedMinor > data.outstandingMinor;
  const exact = typedMinor !== null && typedMinor === data.outstandingMinor;

  const take = () => {
    const value = readAmount(amount);
    if (value.minor === null) {
      setAmountError(value.error);
      return;
    }
    if (chosen === null) return;
    setAmountError(null);
    void submission
      .run(() =>
        api.createPayment(orderId, {
          purpose,
          amountMinor: value.minor,
          processor: chosen,
          idempotencyKey: key.current,
        }),
      )
      .then((result) => {
        if (result === null) return;
        setMadeLink(result.payUrl);
        setAmount('');
        key.current = idempotencyKey();
        paid.reload();
      });
  };

  return (
    <section className="pt-section" aria-label={t('pay.heading')}>
      <h2 className="pt-cutsheet__blockTitle">{t('pay.heading')}</h2>
      <Statement>{t('pay.lede')}</Statement>

      {/* ── the three figures, and the server's own sentence about them ─── */}
      <dl>
        <FactPair label={t('pay.value')}>
          {money(data.takenMinor + data.outstandingMinor, data.currency, true)}
        </FactPair>
        <FactPair label={t('pay.taken')}>{money(data.takenMinor, data.currency, true)}</FactPair>
        <FactPair label={t('pay.outstanding')}>
          {money(data.outstandingMinor, data.currency, true)}
        </FactPair>
      </dl>
      {data.outstandingMinor <= 0 && data.takenMinor > 0 ? (
        <FactLine>{t('pay.settled')}</FactLine>
      ) : null}
      {/* Rendered, not re-typed: the "this is addition, not a policy" sentence
          belongs to the server, which is the thing that did the addition. */}
      <FactLine>{data.termsStatement}</FactLine>

      {/* ── what has been taken so far ──────────────────────────────────── */}
      {data.payments.length === 0 ? (
        <Statement>{t('pay.none')}</Statement>
      ) : (
        <ul>
          {data.payments.map((intent) => (
            <IntentRow key={intent.paymentIntentId} intent={intent} onWrote={paid.reload} />
          ))}
        </ul>
      )}

      {/* ── taking one, in the room ─────────────────────────────────────── */}
      <section className="pt-block">
        <h3 className="pt-record__groupHeading">{t('pay.take.heading')}</h3>

        <TextInput
          id="pt-pay-amount"
          label={t('pay.take.amount.label')}
          hint={t('pay.take.amount.help')}
          value={amount}
          onChange={(next) => {
            setAmount(next);
            setAmountError(null);
          }}
          inputMode="decimal"
          autoComplete="off"
          {...(amountError === null ? {} : { error: amountError })}
        />
        {/* NAMED, NOT REFUSED. What a house will take above its own price is
            Paul's sentence; the arithmetic is ours. */}
        {over ? (
          <FactLine>
            {t('pay.take.over', {
              overage: money((typedMinor ?? 0) - data.outstandingMinor, data.currency, true),
            })}
          </FactLine>
        ) : null}
        {exact ? <FactLine>{t('pay.take.exact')}</FactLine> : null}

        <fieldset className="pt-filter">
          <legend className="pt-filter__legend">{t('pay.take.purposeLabel')}</legend>
          <RovingRadioGroup
            ariaLabel={t('pay.take.purposeLabel')}
            values={PURPOSES}
            chosen={purpose}
            labelOf={(value) => tDynamic(`pay.take.purpose.${value}`)}
            onChoose={setPurpose}
          />
        </fieldset>

        {/* IT ASKS BEFORE IT OFFERS. A path that fails while a client is holding
            out a card is worse than one never offered, so an adapter that is not
            configured is not drawn as a choice — it is stated, with the
            adapter's own reason, and cannot be picked. */}
        <fieldset className="pt-filter">
          <legend className="pt-filter__legend">{t('pay.take.processorLabel')}</legend>
          {usable.length === 0 ? null : (
            <RovingRadioGroup
              ariaLabel={t('pay.take.processorLabel')}
              values={usable.map((m) => m.processor)}
              chosen={chosen ?? ''}
              labelOf={(value) => value}
              onChoose={setProcessor}
            />
          )}
          {unusable.map((method: PaymentMethod) => (
            <FactLine key={method.processor}>
              {t('pay.method.notready', {
                processorName: method.processor,
                reason: method.statement,
              })}
            </FactLine>
          ))}
        </fieldset>
        {methods.data === null ? null : <FactLine>{methods.data.statement}</FactLine>}

        {submission.rejection === null ? null : <RefusalStatement rejection={submission.rejection} />}
        {submission.offline ? <OfflineStatement /> : null}

        {madeLink === null ? null : (
          <>
            <FactLine label={t('pay.paylink.heading')}>{madeLink}</FactLine>
            <FactLine>{t('pay.paylink.help')}</FactLine>
          </>
        )}

        <div className="pt-actions">
          <Button onClick={take} busy={submission.busy}>
            {t('pay.take.submit')}
          </Button>
        </div>
      </section>

      <FactLine>{t('pay.append.statement')}</FactLine>
    </section>
  );
}

/**
 * One intent, its running total and everything that has happened to it — plus
 * the two things a tailor can still do, which are both APPENDS. There is no
 * edit and no delete at any path for any role; a mistake is corrected by
 * recording what really happened next.
 */
function IntentRow({ intent, onWrote }: { intent: PaymentIntentRow; onWrote: () => void }) {
  const submission = useSubmission();
  const [kind, setKind] = useState<'captured' | 'refunded' | null>(null);
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const key = useRef(idempotencyKey());

  const outstandingOnIntent = intent.amountMinor - intent.capturedMinor;
  const givenBackCeiling = intent.capturedMinor - intent.refundedMinor;

  const write = () => {
    if (kind === null) return;
    const read = minorFromTyped(amount);
    if (!('minor' in read)) {
      setAmountError(tDynamic(`pay.amount.${read.refused}`));
      return;
    }
    setAmountError(null);
    void submission
      .run(() =>
        api.recordPaymentEvent(intent.paymentIntentId, {
          kind,
          amountMinor: read.minor,
          idempotencyKey: key.current,
        }),
      )
      .then((result) => {
        if (result === null) return;
        setKind(null);
        setAmount('');
        key.current = idempotencyKey();
        onWrote();
      });
  };

  return (
    <li className="pt-row">
      <FactLine emphasis>
        {t('pay.intent.line', {
          purposeLabel: tDynamic(`pay.take.purpose.${intent.purpose}`),
          amount: money(intent.amountMinor, intent.currency, true),
          processorName: intent.processor,
        })}
      </FactLine>
      <FactLine>{tDynamic(`pay.intent.state.${intent.state}`)}</FactLine>
      <FactLine>{t('pay.intent.reference', { reference: intent.reference })}</FactLine>
      {intent.capturedMinor === 0 ? null : (
        <FactLine>
          {t('pay.intent.taken', { amount: money(intent.capturedMinor, intent.currency, true) })}
        </FactLine>
      )}
      {intent.refundedMinor === 0 ? null : (
        <FactLine>
          {t('pay.intent.refunded', {
            amount: money(intent.refundedMinor, intent.currency, true),
          })}
        </FactLine>
      )}

      <ol>
        {intent.events.map((event) => (
          <li key={event.seq} className="pt-timeline__entry">
            <span>
              {t('pay.intent.event', {
                kindLabel: tDynamic(`pay.event.${event.kind}`),
                authorName: event.actorDisplayName ?? t('global.houseName'),
                at: dateInWords(event.at),
              })}
            </span>
            {event.amountMinor === null ? null : (
              <span>{money(event.amountMinor, intent.currency, true)}</span>
            )}
          </li>
        ))}
      </ol>

      {kind === null ? (
        <div className="pt-actions">
          {outstandingOnIntent > 0 ? (
            <Button
              variant="secondary"
              onClick={() => {
                setKind('captured');
                setAmount('');
              }}
            >
              {t('pay.capture.action')}
            </Button>
          ) : null}
          {givenBackCeiling > 0 ? (
            <Button
              variant="secondary"
              onClick={() => {
                setKind('refunded');
                setAmount('');
              }}
            >
              {t('pay.refund.action')}
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="pt-confirmstep">
          <TextInput
            id={`pt-pay-event-${intent.paymentIntentId}`}
            label={kind === 'captured' ? t('pay.capture.amount.label') : t('pay.refund.amount.label')}
            hint={t('pay.capture.help')}
            value={amount}
            onChange={(next) => {
              setAmount(next);
              setAmountError(null);
            }}
            inputMode="decimal"
            autoComplete="off"
            {...(amountError === null ? {} : { error: amountError })}
          />
          {/* EMPTY ON LOAD, with a labelled one-action fill — `ST-CONFIRM-WINDOW`'s
              rule. A figure a person did not type is a figure nobody checked. */}
          <div className="pt-actions">
            <Button
              variant="secondary"
              onClick={() =>
                setAmount(
                  ((kind === 'captured' ? outstandingOnIntent : givenBackCeiling) / 100).toFixed(2),
                )
              }
            >
              {money(kind === 'captured' ? outstandingOnIntent : givenBackCeiling, intent.currency, true)}
            </Button>
          </div>
          {submission.rejection === null ? null : (
            <RefusalStatement rejection={submission.rejection} />
          )}
          {submission.offline ? <OfflineStatement /> : null}
          <div className="pt-actions">
            <Button onClick={write} busy={submission.busy}>
              {kind === 'captured' ? t('pay.capture.action') : t('pay.refund.action')}
            </Button>
            <Button variant="secondary" onClick={() => setKind(null)}>
              {t('global.action.back')}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
